import { Box, Button, styled, Typography } from '@mui/material';
import type Player from '@vimeo/player'; // eslint-disable-line import/no-unresolved
import type { Options } from '@vimeo/player'; // eslint-disable-line import/no-unresolved
import { useCallback, useEffect, useRef } from 'react';
import arrowLeftAnimated from '../../../../assets/qr/arrow-left-animated.gif';
import arrowLeftDisabled from '../../../../assets/qr/arrow-left-disabled.png';
import arrowRightAnimated from '../../../../assets/qr/arrow-right-animated.gif';
import arrowRightDisabled from '../../../../assets/qr/arrow-right-disabled.png';
import { ENV_WARNING_BAR_HEIGHT } from '../../../../base/components/EnviromentWarningBar/EnviromentWarningBar';
import { Flex } from '../../../../base/components/Flex';
import { useTranslationPrefix } from '../../../../hooks/useTranslationPrefix';

interface Props {
  videoId: string;
  subtitle?: React.ReactNode;
  instruction?: React.ReactNode;
  onNext: () => void;
  direction?: 'left' | 'right';
  loop?: boolean;
}

const QrVideoStep = ({
  videoId,
  subtitle,
  onNext,
  direction,
  loop,
  instruction,
}: Props) => {
  const { _t } = useTranslationPrefix('LaunchPlaylistQrPageNew');
  const videElementRef = useRef<HTMLDivElement>(null);
  const onScriptLoaded = useCallback(() => {
    setTimeout(async () => {
      try {
        //https://www.npmjs.com/package/@vimeo/player?activeTab=readme
        //https://player.vimeo.com/api/demo
        const options: Options = {
          id: Number(videoId),
          loop: !!loop,
          autoplay: true,
          muted: false,
          controls: true,
        };

        const player: Player = new (window as any).Vimeo.Player(
          videElementRef.current || "vimeo-wraper",
          options
        );
        player.on("error", (e) => console.error("Vimeo error:", e));
        player.setVolume(1);

        player.on("loaded", async () => {
          setTimeout(async () => {
            try {
              const width = await player.getVideoWidth();
              const height = await player.getVideoHeight();
              if (width && height && videElementRef.current) {
                (
                  videElementRef.current.style as any
                ).aspectRatio = `${width}/${height}`;
              }
            } catch (e) {
              console.error("Video failed on setting the size ", e);
            }
          }, 250);
        });

        player.on("ended", () => {
          onNext();
        });
      } catch (e) {
        console.error("Video failed on ", e);
      }
    }, 50);
  }, []);

  useEffect(() => {
    //skip if no video
    if (!videoId) {
      onNext();
      return;
    }
    const script = document.createElement('script');
    script.src = 'https://player.vimeo.com/api/player.js';
    script.async = true;
    script.onload = onScriptLoaded;
    document.body.appendChild(script);

    return () => {
      document.body.removeChild(script);
    };
  }, []);

  subtitle = subtitle ? `${subtitle}`.trim() : '';

  return (
    <Flex
      flexDirection="column"
      width="100%"
      minHeight="100%"
      alignItems="center"
      justifyContent="space-between"
      gap={1}
    >
      {subtitle && <Typography textAlign="center">{subtitle}</Typography>}

      <Box
        id="vimeo-wraper"
        ref={videElementRef}
        sx={{
          flexGrow: 0,
          flexShrink: 0,
          width: "100%",
          maxHeight: `calc( 100dvh - ${
            subtitle ? "8rem" : "6rem"
          } - ${ENV_WARNING_BAR_HEIGHT} )`,
          maxWidth: '100%',
          aspectRatio: '16/9',
          overflow: 'hidden',
          position: 'relative',
          '& iframe': {
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
          },
        }}
      >
        <Flex height="100%">
          <Box
            sx={{
              whiteSpace: "pre-wrap",
              padding: 2,
              margin: 'auto',
            }}
          >
            {instruction}
          </Box>
        </Flex>
      </Box>
      <Flex
        flexDirection={{
          xs: direction === 'right' ? 'column-reverse' : 'column',
          sm: 'row',
        }}
        justifyContent={{ xs: 'center', sm: 'space-between' }}
        alignItems="center"
        width="100%"
      >
        {direction && (
          <Box
            sx={{
              position: 'relative',
              display: {
                xs: direction === 'left' ? 'initial' : 'none',
                sm: 'initial',
              },
            }}
          >
            <QrVideoStepLeftArrow
              src={direction === 'left' ? arrowLeftAnimated : arrowLeftDisabled}
            />
          </Box>
        )}
        <Button
          id="video-skip"
          onClick={onNext}
          color="primary"
          variant="text"
          sx={{ mx: 'auto' }}
        >
          {_t('skipVideo', 'Skip')}
        </Button>
        {direction && (
          <Box
            sx={{
              position: 'relative',
              display: {
                xs: direction === 'right' ? 'initial' : 'none',
                sm: 'initial',
              },
            }}
          >
            <QrVideoStepRightArrow
              src={
                direction === 'right' ? arrowRightAnimated : arrowRightDisabled
              }
            />
          </Box>
        )}
      </Flex>
    </Flex>
  );
};

const QrVideoStepRightArrow = styled('img')(
  ({ theme }) => `
    height: 5rem;

    ${theme.breakpoints.up('md')} {
      position: absolute;
      transform: translate(-100%, -50%);
    }
`,
);

const QrVideoStepLeftArrow = styled('img')(
  ({ theme }) => `
    height: 5rem;

    ${theme.breakpoints.up('md')} {
      position: absolute;
      transform: translateY(-50%);
    }
`,
);

export default QrVideoStep;
